<template>
  <AdminLayout>
  <div class="report-manage-page">
    <!-- 헤더 -->
    <div class="page-header">
      <h1 class="page-title">커뮤니티 신고 관리</h1>
      <p class="page-desc">신고된 게시글/댓글을 관리하고 조치를 취할 수 있습니다.</p>
    </div>

    <!-- 통계 카드 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">{{ stats.pending }}</div>
        <div class="stat-label">대기 중</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.resolved }}</div>
        <div class="stat-label">처리 완료</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.dismissed }}</div>
        <div class="stat-label">반려</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.total }}</div>
        <div class="stat-label">전체</div>
      </div>
    </div>

    <!-- 필터 -->
    <div class="filter-section">
      <div class="filter-group">
        <label class="filter-label">유형</label>
        <select v-model="filter.type" class="filter-select" @change="loadReports">
          <option value="all">전체</option>
          <option value="post">게시글</option>
          <option value="comment">댓글</option>
        </select>
      </div>

      <div class="filter-group">
        <label class="filter-label">상태</label>
        <select v-model="filter.status" class="filter-select" @change="loadReports">
          <option value="all">전체</option>
          <option value="pending">대기 중</option>
          <option value="resolved">처리 완료</option>
          <option value="dismissed">반려</option>
        </select>
      </div>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
    </div>

    <!-- 빈 상태 -->
    <div v-else-if="reports.length === 0" class="empty-state">
      <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      <p>신고 내역이 없습니다.</p>
    </div>

    <!-- 신고 목록 -->
    <div v-else class="report-list">
      <div
        v-for="report in reports"
        :key="report.id"
        class="report-card"
        :class="{ blinded: report.contentInfo?.isBlinded }"
      >
        <!-- 상단 정보 -->
        <div class="report-header">
          <div class="report-badges">
            <span :class="['badge', 'badge-type', `badge-${report.type}`]">
              {{ report.type === 'post' ? '게시글' : '댓글' }}
            </span>
            <span :class="['badge', 'badge-status', `badge-${report.status}`]">
              {{ getStatusLabel(report.status) }}
            </span>
            <span v-if="report.contentInfo?.isBlinded" class="badge badge-blinded">
              블라인드
            </span>
          </div>
          <span class="report-time">{{ report.timeAgo }}</span>
        </div>

        <!-- 신고 내용 -->
        <div class="report-content">
          <div class="content-section">
            <h4 class="content-label">신고 대상</h4>
            <template v-if="report.contentInfo">
              <p v-if="report.contentInfo.title" class="content-title">
                {{ report.contentInfo.title }}
              </p>
              <p class="content-text">{{ report.contentInfo.content }}</p>
              <p class="content-author">작성자: {{ report.maskedAuthorEmail }}</p>
            </template>
            <p v-else class="content-deleted">삭제된 콘텐츠</p>
          </div>

          <div class="content-section">
            <h4 class="content-label">신고 사유</h4>
            <p class="reason-badge">{{ getReasonLabel(report.reason) }}</p>
            <p v-if="report.reasonDetail" class="reason-detail">{{ report.reasonDetail }}</p>
          </div>

          <div class="content-meta">
            <span>신고자: {{ report.maskedReporterEmail }}</span>
            <span>누적 신고: {{ report.contentInfo?.reportCount || 0 }}건</span>
          </div>
        </div>

        <!-- 액션 버튼 -->
        <div v-if="report.status === 'pending' && report.contentInfo" class="report-actions">
          <button
            v-if="!report.contentInfo.isBlinded"
            class="action-btn blind"
            @click="handleBlind(report)"
          >
            블라인드
          </button>
          <button
            v-else
            class="action-btn unblind"
            @click="handleUnblind(report)"
          >
            블라인드 해제
          </button>
          <button
            class="action-btn delete"
            @click="handleDelete(report)"
          >
            삭제
          </button>
          <button
            class="action-btn dismiss"
            @click="handleDismiss(report)"
          >
            반려
          </button>
        </div>
      </div>
    </div>

    <!-- 확인 모달 -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">{{ modalTitle }}</h3>
        <p class="modal-message">{{ modalMessage }}</p>
        <div class="modal-actions">
          <button class="modal-btn cancel" @click="closeModal">취소</button>
          <button
            :class="['modal-btn', modalAction === 'delete' ? 'danger' : 'confirm']"
            @click="confirmAction"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  </div>
  </AdminLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { useCommunityReportAdmin } from '@/composables/admin/useCommunityReportAdmin'
import { REPORT_REASONS } from '@/composables/main/useCommunity'

const {
  loading,
  getReports,
  blindContent,
  unblindContent,
  dismissReport,
  deleteContent,
  getReportStats
} = useCommunityReportAdmin()

const reports = ref([])
const stats = ref({
  total: 0,
  pending: 0,
  resolved: 0,
  dismissed: 0
})

const filter = ref({
  type: 'all',
  status: 'pending'
})

// 모달 상태
const showModal = ref(false)
const modalTitle = ref('')
const modalMessage = ref('')
const modalAction = ref('')
const selectedReport = ref(null)

// 상태 라벨
const getStatusLabel = (status) => {
  const labels = {
    pending: '대기 중',
    resolved: '처리 완료',
    dismissed: '반려'
  }
  return labels[status] || status
}

// 신고 사유 라벨
const getReasonLabel = (reasonId) => {
  const reason = REPORT_REASONS.find(r => r.id === reasonId)
  return reason ? reason.label : reasonId
}

// 신고 목록 로드
const loadReports = async () => {
  reports.value = await getReports({
    type: filter.value.type,
    status: filter.value.status
  })
}

// 통계 로드
const loadStats = async () => {
  stats.value = await getReportStats()
}

// 블라인드 처리
const handleBlind = (report) => {
  selectedReport.value = report
  modalTitle.value = '블라인드 처리'
  modalMessage.value = '해당 콘텐츠를 블라인드 처리하시겠습니까? 다른 사용자에게 표시되지 않습니다.'
  modalAction.value = 'blind'
  showModal.value = true
}

// 블라인드 해제
const handleUnblind = (report) => {
  selectedReport.value = report
  modalTitle.value = '블라인드 해제'
  modalMessage.value = '블라인드를 해제하시겠습니까? 다시 다른 사용자에게 표시됩니다.'
  modalAction.value = 'unblind'
  showModal.value = true
}

// 삭제
const handleDelete = (report) => {
  selectedReport.value = report
  modalTitle.value = '콘텐츠 삭제'
  modalMessage.value = '해당 콘텐츠를 완전히 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.'
  modalAction.value = 'delete'
  showModal.value = true
}

// 반려
const handleDismiss = (report) => {
  selectedReport.value = report
  modalTitle.value = '신고 반려'
  modalMessage.value = '해당 신고를 반려하시겠습니까? 신고 내용이 부적절하다고 판단될 때 사용합니다.'
  modalAction.value = 'dismiss'
  showModal.value = true
}

// 모달 닫기
const closeModal = () => {
  showModal.value = false
  selectedReport.value = null
}

// 액션 확인
const confirmAction = async () => {
  if (!selectedReport.value) return

  const report = selectedReport.value
  let result

  switch (modalAction.value) {
    case 'blind':
      result = await blindContent(
        report.id,
        report.type,
        report.type === 'post' ? report.postId : report.commentId
      )
      break
    case 'unblind':
      result = await unblindContent(
        report.id,
        report.type,
        report.type === 'post' ? report.postId : report.commentId
      )
      break
    case 'delete':
      result = await deleteContent(
        report.id,
        report.type,
        report.type === 'post' ? report.postId : report.commentId,
        report.postId
      )
      break
    case 'dismiss':
      result = await dismissReport(report.id)
      break
  }

  if (result?.success) {
    closeModal()
    loadReports()
    loadStats()
  } else {
    alert(result?.error || '처리 중 오류가 발생했습니다.')
  }
}

onMounted(() => {
  loadReports()
  loadStats()
})
</script>

<style scoped>
.report-manage-page {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

/* 헤더 */
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.page-desc {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* 통계 카드 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #2563eb;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
}

/* 필터 */
.filter-section {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.filter-select {
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: #2563eb;
}

/* 로딩/빈 상태 */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
}

/* 신고 목록 */
.report-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.report-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.report-card.blinded {
  border-left: 4px solid #f97316;
}

/* 신고 헤더 */
.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.report-badges {
  display: flex;
  gap: 8px;
}

.badge {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
}

.badge-type.badge-post {
  background: #dbeafe;
  color: #2563eb;
}

.badge-type.badge-comment {
  background: #fef3c7;
  color: #d97706;
}

.badge-status.badge-pending {
  background: #fef3c7;
  color: #d97706;
}

.badge-status.badge-resolved {
  background: #d1fae5;
  color: #059669;
}

.badge-status.badge-dismissed {
  background: #f1f5f9;
  color: #64748b;
}

.badge-blinded {
  background: #fee2e2;
  color: #dc2626;
}

.report-time {
  font-size: 13px;
  color: #94a3b8;
}

/* 신고 내용 */
.report-content {
  margin-bottom: 16px;
}

.content-section {
  margin-bottom: 16px;
}

.content-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 8px 0;
  text-transform: uppercase;
}

.content-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.content-text {
  font-size: 14px;
  color: #475569;
  margin: 0;
  line-height: 1.5;
  background: #f8fafc;
  padding: 12px;
  border-radius: 8px;
}

.content-author {
  font-size: 13px;
  color: #94a3b8;
  margin: 8px 0 0 0;
}

.content-deleted {
  font-size: 14px;
  color: #94a3b8;
  font-style: italic;
  margin: 0;
}

.reason-badge {
  display: inline-block;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #dc2626;
  background: #fee2e2;
  border-radius: 6px;
  margin: 0;
}

.reason-detail {
  font-size: 14px;
  color: #475569;
  margin: 8px 0 0 0;
}

.content-meta {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #64748b;
}

/* 액션 버튼 */
.report-actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.action-btn {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.blind {
  background: #f97316;
  color: white;
}

.action-btn.blind:hover {
  background: #ea580c;
}

.action-btn.unblind {
  background: #10b981;
  color: white;
}

.action-btn.unblind:hover {
  background: #059669;
}

.action-btn.delete {
  background: #ef4444;
  color: white;
}

.action-btn.delete:hover {
  background: #dc2626;
}

.action-btn.dismiss {
  background: #f1f5f9;
  color: #64748b;
}

.action-btn.dismiss:hover {
  background: #e2e8f0;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  margin: 16px;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.modal-message {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 24px 0;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.modal-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.cancel {
  background: #f1f5f9;
  color: #64748b;
}

.modal-btn.cancel:hover {
  background: #e2e8f0;
}

.modal-btn.confirm {
  background: #2563eb;
  color: white;
}

.modal-btn.confirm:hover {
  background: #1d4ed8;
}

.modal-btn.danger {
  background: #ef4444;
  color: white;
}

.modal-btn.danger:hover {
  background: #dc2626;
}

/* 반응형 */
@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-section {
    flex-direction: column;
  }
}
</style>
